import React, { useState, useEffect, useCallback } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as SpeciesService from '../services/SpeciesService';
import type { SpeciesWithTranslation } from '../services/SpeciesService';
import { isLargeSpecies } from '../services/SpeciesService';
import { logCatch } from '../services/FishingService';
import { displayToGrams, displayToMm, displayToMmDepth, getLengthUnit, getWeightUnit, getDepthUnit } from '../utils/unitConversion';
import { takePhoto, uploadPhoto } from '../services/PhotoService';
import * as FishingDB from '../services/FishingDB';
import MapView from './MapView';
import ModalWrapper from './ModalWrapper';
import '../styles/QuickCatchModal.css';

interface QuickCatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string, type?: 'info' | 'success' | 'error') => void;
}

export const QuickCatchModal: React.FC<QuickCatchModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { unitSystem, defaultSpecies } = useSettings();
  const { language, t } = useLanguage();
  
  // Species list from DB with translations
  const [speciesList, setSpeciesList] = useState<SpeciesWithTranslation[]>([]);
  const [selectedSpeciesKey, setSelectedSpeciesKey] = useState<string>('');
  const [isLarge, setIsLarge] = useState(false);
  
  // Methods and Tackle
  const [methodsList, setMethodsList] = useState<Array<{key: string; isCustom: boolean}>>([]);
  const [tackleList, setTackleList] = useState<Array<{key: string; isCustom: boolean}>>([]);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [selectedTackle, setSelectedTackle] = useState<string[]>([]);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showAddTackle, setShowAddTackle] = useState(false);
  const [newMethodName, setNewMethodName] = useState('');
  const [newTackleName, setNewTackleName] = useState('');
  
  // Basic inputs
  const [count, setCount] = useState<number>(1);
  const [caughtType, setCaughtType] = useState<'caught' | 'contact'>('caught');
  
  // Advanced inputs (hidden by default)
  const [showDetails, setShowDetails] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false); // NEW: Avancerad toggle
  const [weight, setWeight] = useState<string>('');
  const [length, setLength] = useState<string>('');
  const [fishingDepth, setFishingDepth] = useState<string>(''); // Depth in meters/feet
  const [notes, setNotes] = useState<string>('');
  const [waterTemp, setWaterTemp] = useState<string>('');
  const [photo, setPhoto] = useState<string>('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  // Advanced: custom date/time, location, session
  const [customDate, setCustomDate] = useState<string>('');
  const [customTime, setCustomTime] = useState<string>('');
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [selectedAreaId, setSelectedAreaId] = useState<string>('');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [customLat, setCustomLat] = useState<number | null>(null);
  const [customLng, setCustomLng] = useState<number | null>(null);
  const [userPosition, setUserPosition] = useState<{lat: number; lng: number} | null>(null);
  
  // Lists for advanced options
  const [sessionsList, setSessionsList] = useState<any[]>([]);
  const [areasList, setAreasList] = useState<any[]>([]);
  const [hasAutoSelectedSession, setHasAutoSelectedSession] = useState(false); // Track if we've done auto-selection
  
  const [isLoading, setIsLoading] = useState(false);

  // Load functions (defined before useEffects that use them)
  const loadSpecies = useCallback(async () => {
    try {
      const allSpecies = await SpeciesService.getAllSpecies((key: string) => t[key as keyof typeof t] as string, language);
      setSpeciesList(allSpecies);
    } catch (error) {
      console.error('Error loading species:', error);
    }
  }, [t, language]);

  const loadSessions = async () => {
    try {
      const sessions = await FishingDB.db.sessions.toArray();
      setSessionsList(sessions || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadAreas = async () => {
    try {
      const areas = await FishingDB.db.areas.toArray();
      setAreasList(areas || []);
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  };

  const loadMethods = async () => {
    try {
      const methods = await FishingDB.db.methods.toArray();
      setMethodsList(methods || []);
    } catch (error) {
      console.error('Error loading methods:', error);
    }
  };

  const loadTackle = async () => {
    try {
      const tackle = await FishingDB.db.tackle.toArray();
      setTackleList(tackle || []);
    } catch (error) {
      console.error('Error loading tackle:', error);
    }
  };

  // Load species from DB and set defaults
  useEffect(() => {
    if (isOpen) {
      loadSpecies();
      loadSessions(); // Always load fresh sessions when modal opens
      loadAreas();
      loadMethods();
      loadTackle();
      
      // Set current date/time as default when opening
      const now = new Date();
      setCustomDate(now.toISOString().split('T')[0]); // YYYY-MM-DD
      setCustomTime(now.toTimeString().split(' ')[0].substring(0, 5)); // HH:MM
    }
  }, [isOpen, loadSpecies]);
  
  // Reset auto-selection flag when modal opens or advanced mode is toggled off
  useEffect(() => {
    if (isOpen || !showAdvanced) {
      setHasAutoSelectedSession(false); // Reset flag for fresh auto-selection
    }
  }, [isOpen, showAdvanced]);
  
  // Set active session as default when advanced mode opens FIRST TIME (if there is one)
  // User can still manually change to "Auto-skapa nytt pass"
  useEffect(() => {
    if (showAdvanced && sessionsList.length > 0 && !hasAutoSelectedSession) {
      const activeSession = sessionsList.find(s => s.isActive && !s.endTime);
      if (activeSession) {
        setSelectedSessionId(activeSession.id);
        console.log('📝 Auto-selected active session:', activeSession.id);
        setHasAutoSelectedSession(true); // Only mark as done if we actually selected something
      } else {
        // No active session, keep default "Auto-skapa nytt pass" (empty string)
        console.log('📝 No active session, defaulting to "Auto-skapa nytt pass"');
        setHasAutoSelectedSession(true); // Mark as done so we don't keep checking
      }
    }
  }, [showAdvanced, sessionsList, hasAutoSelectedSession]);

  // Get user's current position when map picker is opened
  useEffect(() => {
    if (showMapPicker && !userPosition) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserPosition(pos);
          // Set as default location if no custom location selected yet
          if (!customLat && !customLng) {
            setCustomLat(pos.lat);
            setCustomLng(pos.lng);
            console.log('📍 Auto-placed marker at user position:', pos);
          }
        },
        (error) => {
          console.error('Error getting position:', error);
        },
        { timeout: 5000, enableHighAccuracy: true }
      );
    }
  }, [showMapPicker, userPosition, customLat, customLng]);

  // Set default species on open
  useEffect(() => {
    if (isOpen && defaultSpecies && speciesList.length > 0) {
      setSelectedSpeciesKey(defaultSpecies);
    }
  }, [isOpen, defaultSpecies, speciesList]);

  // Check if selected species is large (for quick-select buttons)
  useEffect(() => {
    if (selectedSpeciesKey) {
      isLargeSpecies(selectedSpeciesKey).then(setIsLarge);
    }
  }, [selectedSpeciesKey]);

  // Photo handlers
  const handleTakePhoto = async () => {
    if (caughtType !== 'caught') return;
    setIsUploadingPhoto(true);
    try {
      const photoData = await takePhoto();
      if (photoData) {
        setPhoto(photoData.base64 || '');
        onSuccess?.(t.photoCaptured || '📷 Foto taget!', 'success');
      } else {
        // User cancelled - this is OK, just reset state
        console.log('Photo capture cancelled by user');
      }
    } catch (error) {
      console.error('Failed to take photo:', error);
      onSuccess?.(t.photoCaptureFailed || '❌ Kunde inte ta foto', 'error');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleUploadPhoto = async () => {
    if (caughtType !== 'caught') return;
    setIsUploadingPhoto(true);
    try {
      const photoData = await uploadPhoto();
      if (photoData) {
        setPhoto(photoData.base64 || '');
        onSuccess?.(t.photoUploaded || '🖼️ Foto uppladdat!', 'success');
      } else {
        // User cancelled - this is OK, just reset state
        console.log('Photo upload cancelled by user');
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      onSuccess?.(t.photoUploadFailed || '❌ Kunde inte ladda upp foto', 'error');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto('');
    onSuccess?.(t.photoRemoved, 'info');
  };

  // Method and Tackle handlers
  const handleAddMethod = async () => {
    if (!newMethodName.trim()) return;
    
    try {
      const newMethod = {
        id: FishingDB.generateId(),
        key: newMethodName.trim(),
        isCustom: true,
        createdAt: Date.now()
      };
      
      await FishingDB.db.methods.add(newMethod);
      await loadMethods();
      setSelectedMethod(newMethod.key);
      setNewMethodName('');
      setShowAddMethod(false);
      onSuccess?.(t.added || 'Tillagd!', 'success');
    } catch (error) {
      console.error('Failed to add method:', error);
      onSuccess?.(t.error || 'Fel!', 'error');
    }
  };

  const handleAddTackle = async () => {
    if (!newTackleName.trim()) return;
    
    try {
      const newTackle = {
        id: FishingDB.generateId(),
        key: newTackleName.trim(),
        isCustom: true,
        createdAt: Date.now()
      };
      
      await FishingDB.db.tackle.add(newTackle);
      await loadTackle();
      setSelectedTackle(prev => [...prev, newTackle.key]);
      setNewTackleName('');
      setShowAddTackle(false);
      onSuccess?.(t.added || 'Tillagd!', 'success');
    } catch (error) {
      console.error('Failed to add tackle:', error);
      onSuccess?.(t.error || 'Fel!', 'error');
    }
  };

  const toggleTackle = (tackleKey: string) => {
    setSelectedTackle(prev => {
      if (prev.includes(tackleKey)) {
        return prev.filter(t => t !== tackleKey);
      } else {
        return [...prev, tackleKey];
      }
    });
  };

  const getMethodDisplayName = (method: {key: string; isCustom: boolean}): string => {
    if (method.isCustom) return method.key;
    // method.key is already "methodSpin", "methodBait", etc. - use directly
    const translationKey = method.key as keyof typeof t;
    return (t[translationKey] as string) || method.key;
  };

  const getTackleDisplayName = (tackle: {key: string; isCustom: boolean}): string => {
    if (tackle.isCustom) return tackle.key;
    // tackle.key is already "tackleWobbler", "tackleSoftbait", etc. - use directly
    const translationKey = tackle.key as keyof typeof t;
    return (t[translationKey] as string) || tackle.key;
  };

  // Quick-select buttons for weight
  const getWeightQuickSelects = () => {
    if (unitSystem === 'metric') {
      // kg for metric
      return isLarge 
        ? [1, 2, 3, 4, 5, 7, 10] // Large species: kg
        : [0.05, 0.1, 0.2, 0.3, 0.5, 0.8, 1]; // Small species: kg
    } else {
      // lbs for imperial
      return isLarge
        ? [2, 4, 6, 10, 15, 20, 25] // Large species: lbs
        : [0.1, 0.25, 0.5, 1, 2, 3, 4]; // Small species: lbs
    }
  };

  // Quick-select buttons for length
  const getLengthQuickSelects = () => {
    if (unitSystem === 'metric') {
      // cm for metric
      return isLarge
        ? [30, 40, 50, 60, 70, 80, 90, 100, 110] // Large species: cm
        : [10, 15, 20, 25, 30]; // Small species: cm
    } else {
      // inches for imperial
      return isLarge
        ? [12, 16, 20, 24, 30, 36, 40] // Large species: inches
        : [4, 6, 8, 10, 12]; // Small species: inches
    }
  };

  const handleSubmit = async () => {
    if (!selectedSpeciesKey) {
      onSuccess?.(t.selectSpecies, 'error');
      return;
    }

    // Validate that custom date is not in the future (only if advanced mode is open)
    if (showAdvanced && customDate) {
      const selectedDateTime = new Date(customDate + 'T' + (customTime || '00:00'));
      const now = new Date();
      
      if (selectedDateTime > now) {
        onSuccess?.(t.cannotLogFutureDate || '❌ Kan inte logga fångst i framtiden', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      // Convert display units to DB units (grams & mm)
      const weightInGrams = weight ? displayToGrams(parseFloat(weight), unitSystem) : undefined;
      const lengthInMm = length ? displayToMm(parseFloat(length), unitSystem) : undefined;
      const fishingDepthInMm = fishingDepth ? displayToMmDepth(parseFloat(fishingDepth), unitSystem) : undefined;
      const waterTempNum = waterTemp ? parseFloat(waterTemp) : undefined;

      // Handle custom timestamp (only if advanced mode is open and date/time is set)
      let customTimestamp: number | undefined;
      if (showAdvanced && (customDate || customTime)) {
        const now = new Date();
        const dateStr = customDate || now.toISOString().split('T')[0]; // Use today if no date
        const timeStr = customTime || now.toTimeString().split(' ')[0].substring(0, 5); // Use now if no time
        const dateTimeStr = `${dateStr}T${timeStr}:00`;
        customTimestamp = new Date(dateTimeStr).getTime();
      }
      // If !showAdvanced or no custom date/time, customTimestamp stays undefined

      // Handle session selection (only if advanced mode is open)
      let sessionIdToUse: string | undefined;
      if (showAdvanced) {
        if (selectedSessionId === 'none') {
          sessionIdToUse = undefined; // No session
          console.log('📝 No session selected');
        } else if (selectedSessionId === '') {
          // Auto-create session for this catch - startSession will end active sessions first
          console.log('🔄 Auto-creating new session (will end active sessions first)...');
          const { startSession } = await import('../services/FishingService');
          const newSession = await startSession({ autoCreated: true });
          sessionIdToUse = newSession.id;
          console.log('✅ Auto-created new session:', newSession.id, 'isActive:', newSession.isActive);
        } else {
          sessionIdToUse = selectedSessionId; // Use selected session
          console.log('📝 Using existing session:', selectedSessionId);
        }
      }
      // If !showAdvanced, sessionIdToUse stays undefined and logCatch will use active session

      // Handle area selection (only if advanced mode is open)
      let locationOverride: { lat?: number; lng?: number; fishingAreaId?: string } | undefined;
      if (showAdvanced) {
        if (selectedAreaId === 'none') {
          // No location
          locationOverride = { lat: undefined, lng: undefined, fishingAreaId: undefined };
        } else if (selectedAreaId === 'create-new') {
          // Create new area with custom location from map
          if (customLat && customLng) {
            locationOverride = {
              lat: customLat,
              lng: customLng,
              fishingAreaId: undefined // Will be auto-created by assignOrCreateFishingArea
            };
          } else {
            // No location selected on map yet
            onSuccess?.(t.chooseOnMap, 'error');
            setIsLoading(false);
            return;
          }
        } else if (selectedAreaId && selectedAreaId !== '') {
          // Use selected area
          const selectedArea = areasList.find(a => a.id === selectedAreaId);
          if (selectedArea) {
            locationOverride = {
              lat: selectedArea.centerLat,
              lng: selectedArea.centerLng,
              fishingAreaId: selectedArea.id
            };
          }
        }
        // If selectedAreaId === '', use GPS (default behavior, no override)
      }
      // If !showAdvanced, locationOverride stays undefined and logCatch will use GPS

      await logCatch({
        species: selectedSpeciesKey,
        count,
        caught: caughtType === 'caught',
        weight: weightInGrams,
        length: lengthInMm,
        fishingDepth: fishingDepthInMm,
        method: selectedMethod || undefined,
        tackle: selectedTackle.length > 0 ? selectedTackle : undefined,
        notes: notes || undefined,
        waterTemp: waterTempNum,
        photo: photo || undefined,
        customTimestamp, // Pass custom timestamp for backlog
        sessionId: sessionIdToUse, // Pass explicit session ID
        ...locationOverride // Spread location override if provided
      });

      // Success!
      const speciesName = speciesList.find(s => s.key === selectedSpeciesKey)?.name || selectedSpeciesKey;
      const emoji = caughtType === 'caught' ? '🎣' : '💨';
      onSuccess?.(
        count > 1 
          ? `${emoji} ${count} ${speciesName} ${t.catchesLogged}!`
          : `${emoji} ${count} ${speciesName} ${t.catchLogged}!`,
        'success'
      );

      // Reset and close
      handleReset();
      onClose();
    } catch (error) {
      console.error('Failed to log catch:', error);
      onSuccess?.(t.catchLogFailed, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCount(1);
    setCaughtType('caught');
    setShowDetails(false);
    setShowAdvanced(false);
    setWeight('');
    setLength('');
    setFishingDepth('');
    setSelectedMethod('');
    setSelectedTackle([]);
    setNotes('');
    setWaterTemp('');
    setPhoto('');
    setCustomDate('');
    setCustomTime('');
    setSelectedSessionId('');
    setSelectedAreaId('');
    setShowMapPicker(false);
    setCustomLat(null);
    setCustomLng(null);
    setUserPosition(null); // Reset user position too
    setHasAutoSelectedSession(false); // Reset auto-selection flag
    setSelectedSpeciesKey(defaultSpecies || '');
    setShowAddMethod(false);
    setShowAddTackle(false);
    setNewMethodName('');
    setNewTackleName('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const footer = (
    <>
      <button onClick={handleClose} className="modal-btn modal-btn-cancel">
        {t.cancelLocation}
      </button>
      <button
        onClick={handleSubmit}
        disabled={!selectedSpeciesKey || isLoading}
        className="modal-btn modal-btn-submit"
        style={{ opacity: (!selectedSpeciesKey || isLoading) ? 0.5 : 1 }}
      >
        {isLoading ? '⏳...' : (count > 1 ? t.logCatches : t.logCatch)}
      </button>
    </>
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      title={t.quickCatchTitle}
      size="md"
      footer={footer}
    >
      {/* Species Selector */}
      <div className="qc-section">
        <label className="qc-label">{t.speciesLabel}</label>
        <div className="qc-species-grid">
          {speciesList.map((species) => (
            <button
              key={species.key}
              onClick={() => setSelectedSpeciesKey(species.key)}
              className={`qc-species-btn ${selectedSpeciesKey === species.key ? 'active' : ''}`}
            >
              <span className="qc-species-emoji">{species.icon}</span>
              <span className="qc-species-name">{species.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Caught Type */}
      <div className="qc-section">
        <label className="qc-label">{t.typeLabel}</label>
        <div className="qc-type-buttons">
          <button
            onClick={() => setCaughtType('caught')}
            className={`qc-type-btn ${caughtType === 'caught' ? 'active' : ''}`}
          >
            {t.typeCaught}
          </button>
          <button
            onClick={() => setCaughtType('contact')}
            className={`qc-type-btn ${caughtType === 'contact' ? 'active' : ''}`}
          >
            {t.typeContact}
          </button>
        </div>
      </div>

      {/* Count */}
      <div className="qc-section">
        <label className="qc-label">{t.countLabel}</label>
        <div className="qc-counter-row">
          <button
            onClick={() => setCount(Math.max(1, count - 1))}
            className="qc-counter-btn"
          >
            −
          </button>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            className="qc-counter-input"
            min="1"
            max="100"
          />
          <button
            onClick={() => setCount(Math.min(100, count + 1))}
            className="qc-counter-btn"
          >
            +
          </button>
        </div>
      </div>

      {/* Toggle Details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="qc-toggle-btn"
      >
        {showDetails ? '▼' : '▶'} {t.moreDetails}
      </button>

      {/* Details Section (collapsible) */}
      {showDetails && (
        <div className="qc-details-section">
          {/* Weight */}
          <div className="qc-input-group">
            <label className="qc-label">{t.weightLabel} ({getWeightUnit(unitSystem)})</label>
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder={t.weightLabel}
              className="qc-input"
              step="0.01"
            />
            {/* Quick-select buttons for weight */}
            <div className="qc-quick-select-grid">
              {getWeightQuickSelects().map((val) => (
                <button
                  key={val}
                  onClick={() => setWeight(val.toString())}
                  className={`qc-quick-select-btn ${weight === val.toString() ? 'active' : ''}`}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>

          {/* Length */}
          <div className="qc-input-group">
            <label className="qc-label">{t.lengthLabel} ({getLengthUnit(unitSystem)})</label>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              placeholder={t.lengthLabel}
              className="qc-input"
              step="0.1"
            />
            {/* Quick-select buttons for length */}
            <div className="qc-quick-select-grid">
              {getLengthQuickSelects().map((val) => (
                <button
                  key={val}
                  onClick={() => setLength(val.toString())}
                  className={`qc-quick-select-btn ${length === val.toString() ? 'active' : ''}`}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>

          {/* Water Temperature */}
          <div className="qc-input-group">
            <label className="qc-label">{t.waterTempLabel}</label>
            <input
              type="number"
              value={waterTemp}
              onChange={(e) => setWaterTemp(e.target.value)}
              placeholder={t.waterTempLabel}
              className="qc-input"
              step="0.1"
            />
          </div>

          {/* Fishing Depth in meters/feet */}
          <div className="qc-input-group">
            <label className="qc-label">{t.depthLabel} ({getDepthUnit(unitSystem)})</label>
            <input
              type="number"
              value={fishingDepth}
              onChange={(e) => setFishingDepth(e.target.value)}
              placeholder={t.depthPlaceholder}
              className="qc-input"
              step="0.1"
            />
          </div>

          {/* Fishing Method (single-select) */}
          <div className="qc-input-group">
            <label className="qc-label">{t.fishingMethod}</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {methodsList.map((method) => (
                <div
                  key={method.key}
                  onClick={() => setSelectedMethod(method.key)}
                  className={`
                    px-3 py-2 rounded-lg text-sm cursor-pointer transition-all
                    ${selectedMethod === method.key 
                      ? 'bg-cyan-500/20 border-2 border-cyan-500/50 text-white' 
                      : 'bg-white/5 border-2 border-white/10 text-slate-300 hover:bg-white/10'}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span>{getMethodDisplayName(method)}</span>
                    {selectedMethod === method.key && <span>⭐</span>}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Add custom method */}
            {!showAddMethod ? (
              <button
                onClick={() => setShowAddMethod(true)}
                className="w-full px-3 py-2 rounded-lg text-sm bg-cyan-600/20 border-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-600/30 transition-all"
              >
                + {t.addMethod}
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMethodName}
                  onChange={(e) => setNewMethodName(e.target.value)}
                  placeholder={t.addMethod}
                  className="qc-input flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddMethod()}
                />
                <button
                  onClick={handleAddMethod}
                  className="px-4 py-2 rounded-lg bg-green-600/20 border-2 border-green-500/30 text-green-400 hover:bg-green-600/30"
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setShowAddMethod(false);
                    setNewMethodName('');
                  }}
                  className="px-4 py-2 rounded-lg bg-red-600/20 border-2 border-red-500/30 text-red-400 hover:bg-red-600/30"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Tackle/Bait (multi-select) */}
          <div className="qc-input-group">
            <label className="qc-label">{t.tackle}</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {tackleList.map((tackle) => (
                <div
                  key={tackle.key}
                  onClick={() => toggleTackle(tackle.key)}
                  className={`
                    px-3 py-2 rounded-lg text-sm cursor-pointer transition-all
                    ${selectedTackle.includes(tackle.key)
                      ? 'bg-cyan-500/20 border-2 border-cyan-500/50 text-white' 
                      : 'bg-white/5 border-2 border-white/10 text-slate-300 hover:bg-white/10'}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span>{getTackleDisplayName(tackle)}</span>
                    {selectedTackle.includes(tackle.key) && <span>⭐</span>}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Add custom tackle */}
            {!showAddTackle ? (
              <button
                onClick={() => setShowAddTackle(true)}
                className="w-full px-3 py-2 rounded-lg text-sm bg-cyan-600/20 border-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-600/30 transition-all"
              >
                + {t.addCustomTackle}
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTackleName}
                  onChange={(e) => setNewTackleName(e.target.value)}
                  placeholder={t.addCustomTackle}
                  className="qc-input flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTackle()}
                />
                <button
                  onClick={handleAddTackle}
                  className="px-4 py-2 rounded-lg bg-green-600/20 border-2 border-green-500/30 text-green-400 hover:bg-green-600/30"
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setShowAddTackle(false);
                    setNewTackleName('');
                  }}
                  className="px-4 py-2 rounded-lg bg-red-600/20 border-2 border-red-500/30 text-red-400 hover:bg-red-600/30"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Photo Upload (only for caught fish) */}
          {caughtType === 'caught' && (
            <div className="qc-input-group">
              <label className="qc-label">{t.photoLabel}</label>
              {photo ? (
                <div className="qc-photo-section">
                  <img src={photo} alt="Catch" className="qc-photo-preview" />
                  <button
                    onClick={() => setPhoto('')}
                    className="qc-photo-remove"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="qc-photo-buttons">
                  <button
                    onClick={handleTakePhoto}
                    disabled={isUploadingPhoto}
                    className="qc-photo-btn"
                  >
                    {t.takePhoto}
                  </button>
                  <button
                    onClick={handleUploadPhoto}
                    disabled={isUploadingPhoto}
                    className="qc-photo-btn"
                  >
                    {t.chooseFromGallery}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="qc-input-group">
            <label className="qc-label">{t.notesLabel}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t.notesPlaceholder}
              className="qc-textarea"
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Toggle Advanced (Backlog) */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="qc-toggle-btn"
      >
        {showAdvanced ? '▼' : '▶'} {t.advancedBacklog}
      </button>

      {/* Advanced Backlog Section */}
      {showAdvanced && (
        <div className="qc-details-section">
          {/* Custom Date */}
          <div className="qc-input-group">
            <label className="qc-label">{t.dateLabel}</label>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]} // Cannot select future dates
              className="qc-input"
            />
            <small style={{ color: '#8b949e', fontSize: '12px' }}>
              {t.datePlaceholder}
            </small>
          </div>

          {/* Custom Time */}
          <div className="qc-input-group">
            <label className="qc-label">{t.timeLabel}</label>
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              max={customDate === new Date().toISOString().split('T')[0] 
                ? new Date().toTimeString().split(' ')[0].substring(0, 5) // Limit to current time if today
                : undefined} // No limit for past dates
              className="qc-input"
            />
            <small style={{ color: '#8b949e', fontSize: '12px' }}>
              {t.timePlaceholder}
            </small>
          </div>

          {/* Session Selection */}
          <div className="qc-input-group">
            <label className="qc-label">{t.sessionLabel}</label>
            <select
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="qc-input"
            >
              <option value="">{t.autoCreateSession}</option>
              <option value="none">{t.noSession}</option>
              
              {/* Active sessions first */}
              {sessionsList.filter(s => s.isActive && !s.endTime).length > 0 && (
                <>
                  <option disabled>{t.activePassSeparator}</option>
                  {sessionsList
                    .filter((s) => s.isActive && !s.endTime)
                    .map((session) => (
                      <option key={session.id} value={session.id}>
                        🟢 {new Date(session.startTime).toLocaleDateString('sv-SE')} {new Date(session.startTime).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                      </option>
                    ))}
                </>
              )}
              
              {/* Completed sessions */}
              {sessionsList.filter(s => s.endTime).length > 0 && (
                <>
                  <option disabled>{t.completedPassSeparator}</option>
                  {sessionsList
                    .filter((s) => s.endTime)
                    .sort((a, b) => b.startTime - a.startTime) // Newest first
                    .slice(0, 10) // Only show last 10
                    .map((session) => (
                      <option key={session.id} value={session.id}>
                        ⚪ {new Date(session.startTime).toLocaleDateString('sv-SE')} {new Date(session.startTime).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })} 
                        {session.endTime && ` - ${new Date(session.endTime).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`}
                      </option>
                    ))}
                </>
              )}
            </select>
            <small style={{ color: '#8b949e', fontSize: '12px' }}>
              {t.sessionAutoCreateDescription}<br />
              {t.sessionNoSessionDescription}<br />
              {t.sessionActiveDescription}
            </small>
          </div>

          {/* Area Selection */}
          <div className="qc-input-group">
            <label className="qc-label">{t.areaLabel}</label>
            <select
              value={selectedAreaId}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedAreaId(value);
                // Show map picker when "create-new" is selected
                if (value === 'create-new') {
                  setShowMapPicker(true);
                } else {
                  setShowMapPicker(false);
                  setCustomLat(null);
                  setCustomLng(null);
                  setUserPosition(null); // Reset user position when closing map
                }
              }}
              className="qc-input"
            >
              <option value="">{t.useGPS}</option>
              <option value="create-new">{t.customLocation}</option>
              <option value="none">{t.noArea}</option>
              {areasList.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name || `Fiskeområde (${area.centerLat.toFixed(4)}, ${area.centerLng.toFixed(4)})`}
                </option>
              ))}
            </select>
            <small style={{ color: '#8b949e', fontSize: '12px' }}>
              {t.useGPSDescription}<br />
              {t.customLocationDescription}<br />
              {t.noAreaDescription}
            </small>
          </div>

          {/* Map Picker - shows when "create-new" is selected */}
          {showMapPicker && (
            <div className="qc-map-picker">
              <label className="qc-label">{t.locationLabel}</label>
              <div style={{ 
                height: '300px', 
                border: '2px solid rgba(139, 148, 158, 0.2)', 
                borderRadius: '8px',
                overflow: 'hidden',
                marginTop: '8px'
              }}>
                <MapView
                  key="map-view" // Prevent unmounting when props change
                  onMapClick={(coords) => {
                    setCustomLat(coords.lat);
                    setCustomLng(coords.lng);
                    console.log(`📍 Selected location: ${coords.lat}, ${coords.lng}`);
                  }}
                  selectedCoordinates={
                    customLat && customLng 
                      ? { lat: customLat, lng: customLng }
                      : null
                  }
                  initialCenter={userPosition || undefined}
                  initialZoom={13}
                  userLocation={userPosition}
                />
              </div>
              {customLat && customLng && (
                <small style={{ color: '#58a6ff', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  ✅ {t.saveLocation}: {customLat.toFixed(4)}, {customLng.toFixed(4)}
                </small>
              )}
            </div>
          )}
        </div>
      )}
    </ModalWrapper>
  );
};

export default QuickCatchModal;
